/**
 * @fileoverview Canvas-based inspection template builder used by system-web
 * and tenant-web authoring surfaces.
 * @module template-builder
 *
 * Responsibilities:
 * - Render the form canvas, form palette, preview surface, and side-rail tools.
 * - Coordinate drag/drop form composition and field placement.
 * - Route template, form, rule, and violation-binding changes to host callbacks.
 * - Keep right-side tool panels bounded inside the editor viewport.
 *
 * Dependencies:
 * - @dnd-kit drag/drop primitives
 * - Local template builder panels and form-builder properties panel
 * - Host-provided local-first services for persistence and library operations
 *
 * Architecture:
 * - UI-only composition component; persistence is callback-driven.
 * - No direct API calls; host apps persist changes through Dexie-first services.
 * - Right-side tool panels are viewport-bounded overlays to avoid horizontal
 *   document scrolling and off-screen controls.
 *
 * Notes:
 * - The side rail must remain visible at the right edge while panels open
 *   leftward into the canvas surface.
 */
import { useState, useCallback, useRef, useEffect, Fragment, useMemo } from 'react';
import {
  DndContext, DragOverlay, PointerSensor, useSensor, useSensors,
  pointerWithin,
} from '@dnd-kit/core';
import type { DragEndEvent, DragOverEvent } from '@dnd-kit/core';
import { useDroppable } from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import type { TemplateBuilderTemplate, TemplateBuilderForm, FormField, LayoutSection, Rule, FormRef, TemplateType, FieldType, ModuleType, ContactsModuleConfig, ViolationsModuleConfig } from '../../types/templateTypes';
import type { DataBoundWidgetDefinition } from '../../types/templateTypes';
import { ModulePickerModal } from './ModulePickerModal';
import type { ViolationCodeSetService } from '../../services/violationCodeSetService';
import type { PanelSelection } from '../form-builder/PropertiesPanel';
import { PropertiesPanel } from '../form-builder/PropertiesPanel';
import { TemplateCanvasToolbar } from './TemplateCanvasToolbar';
import type { CanvasMode } from './TemplateCanvasToolbar';
import { TemplateCanvasFormBlock } from './TemplateCanvasFormBlock';
import { CanvasFieldPalette } from './CanvasFieldPalette';
import { CanvasEmptyState } from './CanvasEmptyState';
import type { FirstFormChoice } from './CanvasEmptyState';
// Section 2.8.3: TemplateSettingsDropdown removed — no import.
import { CanvasRulesPanel } from './CanvasRulesPanel';
import { FormNavSidebar } from './FormNavSidebar';
import { RuleConnectionLines } from './RuleConnectionLines';
import { RuleLinesLegend } from './RuleLinesLegend';
import { computeInternalRuleCounts } from './ruleLinesInventory';
import { CanvasViolationsPanel } from './CanvasViolationsPanel';
import type { ViolationIndexEntry } from '../../types/violationCodeSet';
import type { ViolationMapping } from '../../types/templateTypes';
import { FormLibraryPicker } from './FormLibraryPicker';
import type { FormLibraryService } from './FormLibraryPicker';
import { CanvasAIPanel } from './CanvasAIPanel';
import type { CanvasAIPanelProps } from './CanvasAIPanel';
import { buildInsertedFieldFormUpdate, buildMovedFieldFormUpdates } from './templateCanvasDragUtils';
import type { ViolationAsyncRankFn } from '@firesquirrel/local-db';
import * as formService from '../../services/formFieldUtils';
// Section 2.4: user-saved Widget Library removed — no widgetLibraryService import.
import { useFeatureTier } from '../../contexts/FeatureTierContext';
import { GripVertical, Plus, Check, X as XIcon, FolderOpen, Settings2, GitBranch, FileText, Cpu, Database, AlertTriangle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Local-storage key for panel width
// ---------------------------------------------------------------------------

const PANEL_WIDTH_KEY = 'firesquirrel_canvas_panel_width';
const ACTIVE_PANEL_KEY = 'firesquirrel_canvas_active_panel';
// Legacy keys kept for migration read
const PROPS_WIDTH_KEY = 'firesquirrel_canvas_props_width';
const RULES_WIDTH_KEY = 'firesquirrel_canvas_rules_width';

type ActivePanel = 'properties' | 'rules' | 'violations' | null;

function loadActivePanel(): ActivePanel {
  try {
    const v = localStorage.getItem(ACTIVE_PANEL_KEY);
    if (v === 'properties' || v === 'rules' || v === 'violations') return v;
  } catch { /* ignore */ }
  return null;
}
function saveActivePanel(v: ActivePanel) {
  try { localStorage.setItem(ACTIVE_PANEL_KEY, v ?? ''); } catch { /* ignore */ }
}
const MIN_W = 250;
const MAX_W = 720;
const DEFAULT_W = 280;
const DEFAULT_RULES_W = 260;

function loadW(key: string): number {
  try {
    const v = localStorage.getItem(key);
    if (v) { const n = parseInt(v, 10); if (n >= MIN_W && n <= MAX_W) return n; }
  } catch { /* ignore */ }
  return DEFAULT_W;
}
function saveW(key: string, w: number) {
  try { localStorage.setItem(key, String(Math.round(w))); } catch { /* ignore */ }
}
// ---------------------------------------------------------------------------
// Resize handle
// ---------------------------------------------------------------------------

/**
 * @component ResizeHandle
 *
 * Purpose:
 * - Provides left-edge drag resizing for the active right-side tool panel.
 *
 * Props:
 * - onResize: Receives the horizontal resize delta in pixels.
 *
 * Behavior:
 * - Captures pointer movement during drag and expands when dragged left.
 *
 * Data Source:
 * - local
 *
 * Constraints:
 * - Does not persist width directly; parent owns bounds and persistence.
 *
 * Performance:
 * - Uses refs for pointer state to avoid re-rendering during drag.
 */
function ResizeHandle({ onResize }: { onResize: (delta: number) => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const dragging = useRef(false);
  const lastX = useRef(0);
  const [active, setActive] = useState(false);

  const onPointerDown = useCallback((e: React.PointerEvent) => {
    e.preventDefault();
    dragging.current = true;
    setActive(true);
    lastX.current = e.clientX;
    ref.current?.setPointerCapture?.(e.pointerId);
  }, []);

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    if (!dragging.current) return;
    onResize(lastX.current - e.clientX);
    lastX.current = e.clientX;
  }, [onResize]);

  const onPointerUp = useCallback(() => {
    dragging.current = false;
    setActive(false);
  }, []);

  return (
    <div
      ref={ref}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      aria-label="Resize side panel"
      title="Drag to resize side panel"
      className={`group absolute -left-3 top-0 bottom-0 z-20 flex w-6 cursor-col-resize select-none items-center justify-center touch-none transition-all duration-150 ${
        active ? 'bg-orange-100/90 shadow-[0_0_0_1px_var(--brand-primary)]' : 'bg-orange-50/60 hover:bg-orange-100/80 hover:shadow-[0_0_0_1px_var(--brand-primary)]'
      }`}
    >
      <div
        className={`h-full w-1.5 rounded-full transition-all duration-150 ${
          active ? 'bg-(--brand-primary) shadow-md' : 'bg-orange-400 group-hover:bg-(--brand-primary)'
        }`}
      />
      <div className="absolute top-1/2 flex h-8 -translate-y-1/2 items-center gap-0.5 rounded-full bg-white/80 px-1 py-1 shadow-xs ring-1 ring-orange-200 transition-all duration-150 group-hover:ring-orange-400">
        <span className="h-5 w-0.5 rounded-full bg-orange-400 transition-colors duration-150 group-hover:bg-(--brand-primary)" />
        <span className="h-5 w-0.5 rounded-full bg-orange-400 transition-colors duration-150 group-hover:bg-(--brand-primary)" />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// SortableFormBlock wrapper
// ---------------------------------------------------------------------------

function SortableFormBlockWrapper({
  formRef,
  form,
  isSelected,
  isExpanded,
  isDropHover,
  selection,
  onSelectForm,
  onToggleExpansion,
  onSelectSection,
  onSelectField,
  onRemoveForm,
  onUpdateField,
  onUpdateSection,
  onAddSection,
  onReorderSections,
  onToggleFavorite,
  onUpdateLibraryForm,
  onRemoveFromLibrary,
  onUpdateForm,
  ruleCount,
  internalRuleCount,
  pendingDropSectionId,
  pendingDropFieldType,
  pendingDropInsertIndex,
  activeFieldDropSectionId,
  activeFieldDropInsertIndex,
  showFieldDropTargets,
  onCommitDrop,
  onCancelDrop,
  onRegisterRef,
  isDraggingSection,
  onDeleteField,
  onDuplicateField,
  onUpgradeFormRef,
}: {
  formRef: FormRef;
  form: TemplateBuilderForm;
  isSelected: boolean;
  isExpanded: boolean;
  isDropHover?: boolean;
  selection: PanelSelection;
  onSelectForm: () => void;
  onToggleExpansion: () => void;
  onSelectSection: (id: string) => void;
  onSelectField: (id: string) => void;
  onRemoveForm: () => void;
  onUpdateField: (fieldId: string, updates: Partial<FormField>) => void;
  onUpdateSection: (sectionId: string, updates: Partial<LayoutSection>) => void;
  onAddSection: () => void;
  onReorderSections: (formId: string, newSections: LayoutSection[]) => void;
  onToggleFavorite?: () => void;
  onUpdateLibraryForm?: () => void;
  onRemoveFromLibrary?: () => void;
  onUpdateForm?: (updates: Partial<TemplateBuilderForm>) => void;
  ruleCount: number;
  internalRuleCount?: number;
  pendingDropSectionId?: string | null;
  pendingDropFieldType?: string | null;
  pendingDropInsertIndex?: number | null;
  activeFieldDropSectionId?: string | null;
  activeFieldDropInsertIndex?: number | null;
  showFieldDropTargets?: boolean;
  onCommitDrop?: (result: { label: string; required: boolean }) => void;
  onCancelDrop?: () => void;
  onRegisterRef?: (formId: string, el: HTMLElement | null) => void;
  isDraggingSection?: boolean;
  onDeleteField?: (fieldId: string) => void;
  onDuplicateField?: (fieldId: string, sectionId: string) => void;
  onUpgradeFormRef?: (formId: string, toVersion: number) => void;
}) {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: formRef.formId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  // P2-07: Show upgrade banner when library form has a newer version available.
  const upgradeAvailable = !!(form.librarySourceId && form.version > (formRef.formVersion ?? 0));

  return (
    <div ref={(el) => { setNodeRef(el); onRegisterRef?.(formRef.formId, el); }} style={style}>
      {upgradeAvailable && onUpgradeFormRef && (
        <div className="mb-1 flex items-center justify-between gap-2 px-3 py-1.5 rounded-lg bg-amber-50 border border-amber-200 text-xs">
          <span className="text-amber-700">A newer version of <strong>{form.name}</strong> is available (v{form.version}).</span>
          <button
            onClick={() => onUpgradeFormRef(formRef.formId, form.version)}
            className="shrink-0 px-2 py-0.5 text-[10px] font-semibold bg-amber-500 text-white rounded-sm hover:bg-amber-600 transition-colors"
          >
            Upgrade
          </button>
        </div>
      )}
      <TemplateCanvasFormBlock
        form={form}
        order={formRef.order}
        optional={formRef.optional || false}
        isSelected={isSelected}
        isExpanded={isExpanded}
        isDropHover={!!isDropHover}
        isDraggingSection={isDraggingSection}
        selection={selection}
        onSelectForm={onSelectForm}
        onToggleExpansion={onToggleExpansion}
        onSelectSection={onSelectSection}
        onSelectField={onSelectField}
        onRemoveForm={onRemoveForm}
        onUpdateField={onUpdateField}
        onUpdateSection={onUpdateSection}
        onAddSection={onAddSection}
        onReorderSections={onReorderSections}
        onSaveToLibrary={onToggleFavorite}
        onUpdateLibraryForm={onUpdateLibraryForm}
        onRemoveFromLibrary={onRemoveFromLibrary}
        onUpdateForm={onUpdateForm}
        ruleCount={ruleCount}
        internalRuleCount={internalRuleCount}
        dragHandleProps={{ ...attributes, ...listeners }}
        pendingDropSectionId={pendingDropSectionId}
        pendingDropFieldType={pendingDropFieldType}
        pendingDropInsertIndex={pendingDropInsertIndex}
        activeFieldDropSectionId={activeFieldDropSectionId}
        activeFieldDropInsertIndex={activeFieldDropInsertIndex}
        showFieldDropTargets={showFieldDropTargets}
        onCommitDrop={onCommitDrop}
        onCancelDrop={onCancelDrop}
        onDeleteField={onDeleteField}
        onDuplicateField={onDuplicateField}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewSectionModal — collect section properties before creating on drop
// ---------------------------------------------------------------------------

interface NewSectionConfig {
  title: string;
  columns: 1 | 2 | 3;
  collapsible: boolean;
  defaultCollapsed: boolean;
}

function NewSectionModal({
  onConfirm,
  onCancel,
}: {
  onConfirm: (cfg: NewSectionConfig) => void;
  onCancel: () => void;
}) {
  const [title, setTitle] = useState('');
  const [columns, setColumns] = useState<1 | 2 | 3>(1);
  const [collapsible, setCollapsible] = useState(false);
  const [defaultCollapsed, setDefaultCollapsed] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleConfirm = () => {
    onConfirm({ title: title.trim(), columns, collapsible, defaultCollapsed });
  };

  return (
    <div className="fixed inset-0 z-300 flex items-center justify-center bg-black/30" onClick={onCancel}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-[380px] flex flex-col overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center">
              <Plus size={14} className="text-orange-500" />
            </div>
            <h2 className="text-sm font-semibold text-gray-900">New Section</h2>
          </div>
          <button onClick={onCancel} title="Close" className="p-1 text-gray-400 hover:text-gray-600 rounded-sm transition-colors">
            <XIcon size={15} />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 flex flex-col gap-4">
          {/* Name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Section name</label>
            <input
              ref={inputRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleConfirm(); if (e.key === 'Escape') onCancel(); }}
              placeholder="e.g. General Info, Site Details…"
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg outline-hidden focus:border-(--brand-primary) focus:ring-2 focus:ring-orange-100 transition-colors placeholder-gray-300"
            />
          </div>

          {/* Columns */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1.5">Columns</label>
            <div className="flex gap-2">
              {([1, 2, 3] as const).map((n) => (
                <button
                  key={n}
                  onClick={() => setColumns(n)}
                  className={`flex-1 py-2.5 rounded-lg border-2 text-sm font-medium transition-colors ${
                    columns === n
                      ? 'border-(--brand-primary) bg-orange-50 text-orange-700'
                      : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {n === 1 ? '1 col' : n === 2 ? '2 cols' : '3 cols'}
                </button>
              ))}
            </div>
          </div>

          {/* Collapsible */}
          <div className="flex flex-col gap-2">
            <label className="block text-xs font-medium text-gray-600">Collapsible</label>
            <button
              onClick={() => { setCollapsible((v) => !v); if (collapsible) setDefaultCollapsed(false); }}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 text-xs font-medium transition-colors text-left ${
                collapsible
                  ? 'border-(--brand-primary) bg-orange-50 text-orange-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
              }`}
            >
              <div className={`w-8 h-4.5 rounded-full transition-colors relative flex items-center px-0.5 ${collapsible ? 'bg-(--brand-primary)' : 'bg-gray-200'}`}>
                <div className={`w-3.5 h-3.5 rounded-full bg-white shadow-xs transition-transform ${collapsible ? 'translate-x-3.5' : 'translate-x-0'}`} />
              </div>
              Allow inspectors to collapse this section
            </button>

            {collapsible && (
              <button
                onClick={() => setDefaultCollapsed((v) => !v)}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border-2 text-xs font-medium transition-colors text-left ${
                  defaultCollapsed
                    ? 'border-(--brand-primary) bg-orange-50 text-orange-700'
                    : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-8 h-4.5 rounded-full transition-colors relative flex items-center px-0.5 ${defaultCollapsed ? 'bg-(--brand-primary)' : 'bg-gray-200'}`}>
                  <div className={`w-3.5 h-3.5 rounded-full bg-white shadow-xs transition-transform ${defaultCollapsed ? 'translate-x-3.5' : 'translate-x-0'}`} />
                </div>
                Collapsed by default
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            className="px-4 py-2 text-xs font-medium text-white rounded-lg hover:opacity-90 transition-colors"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CanvasFormDropZone — droppable target shown while dragging a palette form
// ---------------------------------------------------------------------------

function FormInsertSlot({ index, isActive }: { index: number; isActive: boolean }) {
  const id = `form-insert-slot-${index}`;
  const { setNodeRef, isOver } = useDroppable({ id });
  if (!isActive) return null;
  return (
    <div
      ref={setNodeRef}
      className={`w-full rounded-xl border-2 border-dashed transition-all flex items-center justify-center gap-2 text-xs font-medium ${
        isOver
          ? 'border-(--brand-primary) bg-orange-50 text-orange-600 py-4'
          : 'border-gray-200 bg-gray-50/40 text-gray-300 py-2'
      }`}
    >
      <Plus size={13} className={isOver ? 'text-orange-500' : 'text-gray-200'} />
      {isOver ? 'Release to add form here' : 'Drop here'}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TemplateCanvas props
// ---------------------------------------------------------------------------

export interface CanvasFormApiService {
  toggleFormFavorite(formId: string): Promise<TemplateBuilderForm | null>;
  updateForm(formId: string, patch: Partial<TemplateBuilderForm>): Promise<TemplateBuilderForm>;
  propagateLibraryUpdate(formId: string): Promise<number>;
  createForm(data: Partial<TemplateBuilderForm>): Promise<TemplateBuilderForm>;
}

export interface TemplateCanvasProps {
  template: TemplateBuilderTemplate;
  forms: Map<string, TemplateBuilderForm>;
  onUpdateTemplate: (updates: Partial<TemplateBuilderTemplate>) => void;
  onUpdateForm: (formId: string, updates: Partial<TemplateBuilderForm>) => void;
  onAddForm: (choice?: FirstFormChoice) => void;
  onAddFormFromLibrary?: (libraryForm: TemplateBuilderForm) => void;
  onAddModuleForm?: (form: TemplateBuilderForm) => void;
  onRemoveFormRef: (formId: string) => void;
  onReorderForms: (newRefs: FormRef[]) => void;
  onUpgradeFormRef?: (formId: string, toVersion: number) => void;
  onFormsChange?: (forms: Map<string, TemplateBuilderForm>) => void;
  formApiService?: CanvasFormApiService;
  formListService?: { listForms(params?: { reusable?: boolean; templateType?: TemplateType; limit?: number }): Promise<{ items: TemplateBuilderForm[] }> };
  previewRenderer?: React.ComponentType<{ template: TemplateBuilderTemplate; forms: Map<string, TemplateBuilderForm> }>;
  // Section 2.8.6: host app injects these so the Violations tab can run.
  /** Dexie instance used by the embedded ViolationPicker. Optional. */
  dexieDb?: any;
  /** Id of the host app's active (default) violation code set. Optional. */
  activeViolationCodeSetId?: string;
  /** Active code set index entries — used for title resolution + staleness. */
  activeViolationCodeEntries?: ViolationIndexEntry[];
  /** Optional local-first violation ranker used by tenant-web smart search. */
  asyncViolationRanker?: ViolationAsyncRankFn;
  /** Optional host-provided local-first code set service used by system modules. */
  violationCodeSetService?: ViolationCodeSetService;
  /** When true (system-web), Violations module can be confirmed without selecting code sets. */
  allowDeferViolations?: boolean;
  /** Optional host-provided online AI field suggestion handler. */
  onGenerateAISuggestions?: CanvasAIPanelProps['onGenerateSuggestions'];
  /** Optional host-provided online AI document parsing handler. */
  onParseAIDocument?: CanvasAIPanelProps['onParseDocument'];
  /**
   * Called by the canvas after it creates a new blank form through the host-injected formApiService.
   * The host must add the returned form to its local Dexie-backed forms map and append a FormRef
   * to the template. This keeps business logic out of JSX pages (local-first rule §10).
   */
  onNewFormAdded?: (form: TemplateBuilderForm) => void;
  /**
   * Gates every create-then-attach affordance (New Form dialog, Form Library
   * pick, Header Form, System Module — palette items, the bottom add-form bar,
   * and the empty-state choices). Every canvas creation path immediately
   * appends a FormRef through the host patch callback, so hosts should pass
   * the COMBINED capability (`forms.create` AND template edit). When false the
   * controls are disabled/hidden and the create handlers no-op, so a user
   * cannot persist a form that can never be attached. Defaults to true.
   */
  canCreateForms?: boolean;
}

// ---------------------------------------------------------------------------
// TemplateCanvas
// ---------------------------------------------------------------------------

export function TemplateCanvas({
  template,
  forms,
  onUpdateTemplate,
  onUpdateForm,
  onAddForm,
  onAddFormFromLibrary,
  onAddModuleForm,
  onRemoveFormRef,
  onReorderForms,
  onUpgradeFormRef,
  formApiService,
  formListService,
  previewRenderer: PreviewRenderer,
  dexieDb,
  activeViolationCodeSetId,
  activeViolationCodeEntries,
  asyncViolationRanker,
  violationCodeSetService,
  allowDeferViolations,
  onGenerateAISuggestions,
  onParseAIDocument,
  onNewFormAdded,
  canCreateForms = true,
}: TemplateCanvasProps) {
  const [mode, setMode] = useState<CanvasMode>('build');
  const [selectedFormId, setSelectedFormId] = useState<string | null>(null);
  const [selection, setSelection] = useState<PanelSelection>(null);
  const [expandedForms, setExpandedForms] = useState<Set<string>>(new Set());
  const [pendingDrop, setPendingDrop] = useState<{ fieldType: FieldType; sectionId: string; formId: string; insertIndex: number } | null>(null);
  const [panelWidth, setPanelWidth] = useState(() => loadW(PANEL_WIDTH_KEY) || loadW(PROPS_WIDTH_KEY) || DEFAULT_W);
  const [activePanel, setActivePanel] = useState<ActivePanel>(loadActivePanel);
  // Section 2.8.3: settingsOpen removed — dropdown no longer exists.
  const [libraryPickerOpen, setLibraryPickerOpen] = useState(false);
  const [modulePickerOpen, setModulePickerOpen] = useState(false);
  const [saveToast, setSaveToast] = useState<string | null>(null);
  const [showRuleLines, setShowRuleLines] = useState(false);
  // Section 2.6: lifted hover state syncs RuleConnectionLines overlay with the legend panel.
  const [hoveredRuleId, setHoveredRuleId] = useState<string | null>(null);
  const [aiOpen, setAIOpen] = useState(false);
  const [dragOverFormId, setDragOverFormId] = useState<string | null>(null);
  const [isDraggingPaletteForm, setIsDraggingPaletteForm] = useState(false);
  const [isPaletteFormOverSlot, setIsPaletteFormOverSlot] = useState(false);
  const [isDraggingFieldItem, setIsDraggingFieldItem] = useState(false);
  const [activeFieldDropTarget, setActiveFieldDropTarget] = useState<{ formId: string; sectionId: string; insertIndex: number } | null>(null);
  const [isDraggingSection, setIsDraggingSection] = useState(false);
  const [pendingSectionDrop, setPendingSectionDrop] = useState<string | null>(null);
  const [newFormOpen, setNewFormOpen] = useState(false);
  const [newFormName, setNewFormName] = useState('');
  const [newFormDesc, setNewFormDesc] = useState('');
  const [newFormCategory, setNewFormCategory] = useState('');
  const [newFormColumns, setNewFormColumns] = useState<1 | 2 | 3>(1);
  const [newFormHidden, setNewFormHidden] = useState(false);
  const dragAutoExpandedFormId = useRef<string | null>(null);
  /**
   * Ref mirror of activeFieldDropTarget state.
   *
   * Problem: handleDragEnd is memoised by useCallback with a dep-array that
   * does NOT include activeFieldDropTarget (adding it would thrash the callback
   * on every pointer-move). Reading activeFieldDropTarget directly inside the
   * callback therefore yields a stale closure value.
   *
   * Solution: keep a ref that is updated alongside the state setter. Refs are
   * always current inside any closure and are not subject to memoisation
   * staleness, so handleDragEnd can read the last confirmed drag target via
   * this ref without any additional deps.
   *
   * @localFirst Source of Truth: local. No persistence or sync occurs here.
   */
  const activeFieldDropTargetRef = useRef<{ formId: string; sectionId: string; insertIndex: number } | null>(null);
  const { hasAI, aiCostRails, aiTokensUsed, recordAITokens } = useFeatureTier();

  // A2-08: Memoize formListService as FormLibraryService so FormLibraryPicker
  // dep array doesn't trigger infinite reload on every render.
  const memoizedFormLibraryService: FormLibraryService | undefined = useMemo(() => {
    if (!formListService) return undefined;
    return {
      listForms: (params) => formListService.listForms(params),
      toggleFormFavorite: formApiService?.toggleFormFavorite ?? (async () => null),
    };
  }, [formListService, formApiService]);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const canvasScrollRef = useRef<HTMLDivElement>(null);
  const formBlockRefs = useRef<Map<string, HTMLElement>>(new Map());

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  // Sorted form refs
  const sortedRefs = [...(template.formRefs ?? [])].sort((a, b) => a.order - b.order);
  // Section 2.6: count of self-form rules per form — surfaces as the ↻ chip on form cards.
  const internalRuleCounts = useMemo(() => computeInternalRuleCounts(template, forms), [template, forms]);
  const sortedFormIds = useMemo(() => sortedRefs.map((r) => r.formId), [sortedRefs]);
  const hasHeaderForm = sortedRefs.some((r) => forms.get(r.formId)?.formKind === 'header');
  const existingModuleTypes = new Set(
    sortedRefs
      .map((r) => forms.get(r.formId))
      .filter((f) => f?.formKind === 'module' && f.moduleConfig)
      .map((f) => f!.moduleConfig!.moduleType)
  );
  const allModuleTypesUsed = existingModuleTypes.has('contacts') && existingModuleTypes.has('violations');

  const findSectionLocation = useCallback((sectionId: string) => {
    for (const [formId, form] of forms) {
      const section = (form.layout?.sections ?? []).find((item) => item.id === sectionId);
      if (section) return { formId, section };
    }
    return null;
  }, [forms]);

  const resolveFieldDropTarget = useCallback((
    overId: string,
    activeRect?: { top: number; height: number } | null,
    overRect?: { top: number; height: number } | null,
  ) => {
    if (overId.startsWith('field-insert-slot-')) {
      const match = /^field-insert-slot-(.+)-(\d+)$/.exec(overId);
      if (!match) return null;
      const [, sectionId, rawIndex] = match;
      const location = findSectionLocation(sectionId);
      if (!location) return null;
      return {
        formId: location.formId,
        sectionId,
        insertIndex: Math.max(0, Math.min(location.section.fieldIds.length, Number(rawIndex))),
      };
    }

    if (overId.startsWith('field-dropzone-')) {
      const sectionId = overId.replace('field-dropzone-', '');
      const location = findSectionLocation(sectionId);
      if (!location) return null;
      return {
        formId: location.formId,
        sectionId,
        insertIndex: location.section.fieldIds.length,
      };
    }

    for (const [formId, form] of forms) {
      for (const section of form.layout?.sections ?? []) {
        if (section.id === overId) {
          return { formId, sectionId: section.id, insertIndex: section.fieldIds.length };
        }
        const fieldIndex = section.fieldIds.indexOf(overId);
        if (fieldIndex !== -1) {
          const activeCenterY = activeRect ? activeRect.top + activeRect.height / 2 : null;
          const overMidY = overRect ? overRect.top + overRect.height / 2 : null;
          const insertIndex = activeCenterY != null && overMidY != null && activeCenterY >= overMidY
            ? fieldIndex + 1
            : fieldIndex;
          return {
            formId,
            sectionId: section.id,
            insertIndex: Math.max(0, Math.min(section.fieldIds.length, insertIndex)),
          };
        }
      }
    }

    return null;
  }, [findSectionLocation, forms]);

  const syncHoveredForm = useCallback((hoveredFormId: string | null) => {
    if (hoveredFormId && forms.get(hoveredFormId)?.formKind !== 'module' && forms.get(hoveredFormId)?.formKind !== 'header') {
      if (hoveredFormId !== dragOverFormId) {
        if (dragAutoExpandedFormId.current && dragAutoExpandedFormId.current !== hoveredFormId) {
          setExpandedForms((prev) => {
            const next = new Set(prev);
            next.delete(dragAutoExpandedFormId.current!);
            return next;
          });
        }
        dragAutoExpandedFormId.current = hoveredFormId;
        setExpandedForms((prev) => {
          if (prev.has(hoveredFormId)) return prev;
          const next = new Set(prev);
          next.add(hoveredFormId);
          return next;
        });
      }
      setDragOverFormId(hoveredFormId);
      return;
    }

    setDragOverFormId(null);
  }, [forms, dragOverFormId]);

  const getDefaultFieldDropTargetForForm = useCallback((formId: string) => {
    const form = forms.get(formId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return null;
    const sections = form.layout?.sections ?? [];
    if (sections.length === 0) return null;
    const preferredSectionId = selection?.level === 'section' ? selection.sectionId : null;
    const targetSection = sections.find((section) => section.id === preferredSectionId) ?? sections[0];
    return {
      formId,
      sectionId: targetSection.id,
      insertIndex: targetSection.fieldIds.length,
    };
  }, [forms, selection]);

  const resolveInsertFromPointer = useCallback((formId: string, pointerY: number): { formId: string; sectionId: string; insertIndex: number } | null => {
    const form = forms.get(formId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return null;
    const sections = form.layout?.sections ?? [];
    if (sections.length === 0) return null;

    const formBlockEl = formBlockRefs.current.get(formId);
    if (!formBlockEl) return getDefaultFieldDropTargetForForm(formId);

    let bestTarget: { formId: string; sectionId: string; insertIndex: number } | null = null;
    let bestDistance = Number.POSITIVE_INFINITY;

    for (const section of sections) {
      const sectionEl = formBlockEl.querySelector(`[data-section-id="${section.id}"]`);
      if (!sectionEl) continue;

      const fieldEls = Array.from(sectionEl.querySelectorAll<HTMLElement>('[data-field-id]'));
      const slotAnchors = fieldEls.length === 0
        ? [sectionEl.getBoundingClientRect().top + sectionEl.getBoundingClientRect().height / 2]
        : fieldEls.map((fieldEl, index) => {
            const rect = fieldEl.getBoundingClientRect();
            if (index === 0) return rect.top;
            const prevRect = fieldEls[index - 1].getBoundingClientRect();
            return (prevRect.bottom + rect.top) / 2;
          }).concat(fieldEls[fieldEls.length - 1].getBoundingClientRect().bottom);

      slotAnchors.forEach((anchorY, insertIndex) => {
        const distance = Math.abs(pointerY - anchorY);
        if (distance < bestDistance) {
          bestDistance = distance;
          bestTarget = { formId, sectionId: section.id, insertIndex };
        }
      });
    }

    return bestTarget ?? getDefaultFieldDropTargetForForm(formId);
  }, [forms, getDefaultFieldDropTargetForForm]);

  const insertFieldIntoSection = useCallback((formId: string, sectionId: string, insertIndex: number, newField: FormField) => {
    const update = buildInsertedFieldFormUpdate(forms, { formId, sectionId, insertIndex }, newField);
    if (!update) return;

    onUpdateForm(update.formId, update.updates);
    setSelectedFormId(formId);
    setSelection({ level: 'field', fieldId: newField.id });
  }, [forms, onUpdateForm]);

  /**
   * @process MoveCanvasField
   *
   * Steps:
   * 1. Resolve immutable form patches for the dragged field and destination target.
   * 2. Route each patch through the host-provided local-first form update callback.
   * 3. Select and expand the destination form so the user sees the moved field.
   *
   * Inputs:
   * - fieldId from the active dnd-kit draggable item.
   * - fieldDropTarget resolved from current canvas pointer/over state.
   *
   * Outputs:
   * - One same-form layout patch or two cross-form patches passed to onUpdateForm.
   *
   * Failure Handling:
   * - Invalid moves return no patches and leave UI state unchanged.
   *
   * Idempotency: NOT REQUIRED — each valid drop intentionally changes field order.
   */
  const moveFieldIntoSection = useCallback((fieldId: string, target: { formId: string; sectionId: string; insertIndex: number }) => {
    const updates = buildMovedFieldFormUpdates(forms, fieldId, target);
    if (updates.length === 0) return;

    updates.forEach((update) => {
      onUpdateForm(update.formId, update.updates);
    });
    setSelectedFormId(target.formId);
    setExpandedForms((prev) => new Set(prev).add(target.formId));
    setSelection({ level: 'field', fieldId });
  }, [forms, onUpdateForm]);

  // Rules per form (from template-level rules referencing form)
  const rulesPerForm = useCallback((formId: string): number => {
    if (!template.rules) return 0;
    return template.rules.filter((r) => {
      const act = r.then as Array<{ formId?: string }>;
      return act.some((a) => a.formId === formId);
    }).length;
  }, [template.rules]);

  // Expand helpers
  // Card click: expand THIS form, collapse all others
  const handleSelectForm = useCallback((formId: string) => {
    setSelectedFormId(formId);
    setSelection({ level: 'form' });
    setExpandedForms(new Set([formId]));
  }, []);

  // Chevron click: toggle THIS form independently, leave others unchanged
  const handleToggleExpansion = useCallback((formId: string) => {
    setExpandedForms((prev) => {
      const next = new Set(prev);
      if (next.has(formId)) next.delete(formId);
      else next.add(formId);
      return next;
    });
  }, []);

  const handleSelectSection = useCallback((sectionId: string) => {
    setSelection({ level: 'section', sectionId });
  }, []);

  const handleSelectField = useCallback((fieldId: string) => {
    setSelection({ level: 'field', fieldId });
  }, []);

  // Section 2.8.6: reveal a field in the canvas — scroll to its form block,
  // expand that form, select the field, and switch the side-rail to
  // Properties so the Behavior accordion can be edited.
  const revealField = useCallback((formId: string, sectionId: string | undefined, fieldId: string) => {
    setSelectedFormId(formId);
    setExpandedForms((prev) => {
      if (prev.has(formId)) return prev;
      const next = new Set(prev);
      next.add(formId);
      return next;
    });
    setSelection({ level: 'field', fieldId });
    setActivePanel('properties');
    saveActivePanel('properties');
    const el = formBlockRefs.current.get(formId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
    // Suppress unused-var lint: sectionId is accepted for future expansion
    // behavior (e.g. scroll to section anchor) and documented on the API.
    void sectionId;
  }, []);

  // Section 2.8.6: writes a violation mapping onto a field. Passing `null`
  // unlinks the field. Persistence happens via `onUpdateForm` so host apps
  // stay on the local-first Dexie + sync-push path.
  const handleChangeViolationMapping = useCallback((params: {
    formId: string;
    fieldId: string;
    mapping: ViolationMapping | null;
  }) => {
    const { formId, fieldId, mapping } = params;
    const form = forms.get(formId);
    if (!form) return;
    const nextFields = (form.fields ?? []).map((field) => {
      if (field.id !== fieldId) return field;
      return {
        ...field,
        violationMappings: mapping ? [mapping] : [],
      };
    });
    onUpdateForm(formId, { fields: nextFields });
  }, [forms, onUpdateForm]);

  const handleTogglePanel = useCallback((panel: 'properties' | 'rules' | 'violations') => {
    setActivePanel((cur) => {
      const next = cur === panel ? null : panel;
      saveActivePanel(next);
      return next;
    });
  }, []);

  const handleClosePanel = useCallback(() => {
    setActivePanel(null);
    saveActivePanel(null);
  }, []);

  useEffect(() => {
    if (sortedRefs.length === 0) {
      if (selectedFormId !== null) setSelectedFormId(null);
      if (selection !== null) setSelection(null);
      return;
    }

    const formIds = new Set(sortedRefs.map((ref) => ref.formId));
    if (selectedFormId && formIds.has(selectedFormId)) return;

    const fallbackFormId = sortedRefs[0]?.formId ?? null;
    if (!fallbackFormId) return;

    setSelectedFormId(fallbackFormId);
    setSelection({ level: 'form' });
    setExpandedForms((prev) => {
      if (prev.has(fallbackFormId)) return prev;
      return new Set([fallbackFormId]);
    });
    setActivePanel('properties');
    saveActivePanel('properties');
  }, [selectedFormId, selection, sortedRefs]);

  // Field/section update helpers — delegate to the active form
  const handleUpdateField = useCallback((fieldId: string, updates: Partial<FormField>) => {
    if (!selectedFormId) return;
    const form = forms.get(selectedFormId);
    if (!form) return;
    const fields = (form.fields ?? []).map((f) => f.id === fieldId ? { ...f, ...updates } : f);
    onUpdateForm(selectedFormId, { fields });
  }, [selectedFormId, forms, onUpdateForm]);

  const handleUpdateSection = useCallback((sectionId: string, updates: Partial<LayoutSection>) => {
    if (!selectedFormId) return;
    const form = forms.get(selectedFormId);
    if (!form) return;
    const sections = (form.layout?.sections ?? []).map((s) => s.id === sectionId ? { ...s, ...updates } : s);
    onUpdateForm(selectedFormId, { layout: { ...(form.layout ?? {}), sections } });
  }, [selectedFormId, forms, onUpdateForm]);

  const handleDuplicateField = useCallback((formId: string, fieldId: string, sectionId: string) => {
    const form = forms.get(formId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return;
    const source = (form.fields ?? []).find((field) => field.id === fieldId);
    const section = (form.layout?.sections ?? []).find((candidate) => candidate.id === sectionId);
    if (!source || !section) return;

    const newFieldId = `field-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
    const duplicate: FormField = {
      ...JSON.parse(JSON.stringify(source)),
      id: newFieldId,
    };
    if (duplicate.violationMappings) {
      duplicate.violationMappings = duplicate.violationMappings.map((mapping) => ({
        ...mapping,
        trigger: mapping.trigger && 'field' in mapping.trigger && mapping.trigger.field === fieldId
          ? { ...mapping.trigger, field: newFieldId }
          : mapping.trigger,
      }));
    }

    const sourceIndex = section.fieldIds.indexOf(fieldId);
    const nextFieldIds = [...section.fieldIds];
    nextFieldIds.splice(sourceIndex + 1, 0, newFieldId);
    const sections = (form.layout?.sections ?? []).map((candidate) => (
      candidate.id === sectionId ? { ...candidate, fieldIds: nextFieldIds } : candidate
    ));
    onUpdateForm(formId, {
      fields: [...(form.fields ?? []), duplicate],
      layout: { ...(form.layout ?? {}), sections },
    });
    setSelection({ level: 'field', fieldId: newFieldId });
    setSelectedFormId(formId);
    setExpandedForms((prev) => new Set(prev).add(formId));
  }, [forms, onUpdateForm]);

  const handleAddSection = useCallback((formId: string, cfg?: NewSectionConfig) => {
    const form = forms.get(formId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return;
    const newSection: LayoutSection = {
      id: `section-${Date.now()}`,
      title: cfg?.title || '',
      columns: cfg?.columns ?? 1,
      order: (form.layout?.sections ?? []).length,
      fieldIds: [],
      ...(cfg?.collapsible ? { collapsible: true, defaultCollapsed: cfg.defaultCollapsed } : {}),
    };
    onUpdateForm(formId, {
      layout: { ...(form.layout ?? {}), sections: [...(form.layout?.sections ?? []), newSection] },
    });
  }, [forms, onUpdateForm]);

  const handleToggleFavorite = useCallback(async (formId: string) => {
    const form = forms.get(formId);
    if (!form) return;
    if (!formApiService) return;
    const updated = await formApiService.toggleFormFavorite(formId);
    if (!updated) return;
    onUpdateForm(formId, { reusable: updated.reusable });
    const msg = updated.reusable ? `"${updated.name}" added to Form Library` : `"${updated.name}" disassociated from library`;
    setSaveToast(msg);
    setTimeout(() => setSaveToast(null), 3000);
  }, [forms, formApiService, onUpdateForm]);

  const handleUpdateLibraryForm = useCallback(async (formId: string) => {
    const form = forms.get(formId);
    if (!form) return;
    const now = new Date().toISOString();
    if (!formApiService) return;
    await formApiService.updateForm(formId, { reusable: true, updatedAt: now, updatedBy: 'user-001' });
    onUpdateForm(formId, { reusable: true });
    const propagated = await formApiService.propagateLibraryUpdate(formId);
    const extra = propagated > 0 ? ` · ${propagated} other form${propagated !== 1 ? 's' : ''} updated` : '';
    setSaveToast(`"${form.name}" updated in Form Library${extra}`);
    setTimeout(() => setSaveToast(null), 4000);
  }, [forms, formApiService, onUpdateForm]);

  const handleRemoveFromLibrary = useCallback(async (formId: string) => {
    const form = forms.get(formId);
    if (!form) return;
    const now = new Date().toISOString();
    if (!formApiService) return;
    await formApiService.updateForm(formId, { reusable: false, updatedAt: now, updatedBy: 'user-001' });
    onUpdateForm(formId, { reusable: false });
    setSaveToast(`"${form.name}" removed from Form Library`);
    setTimeout(() => setSaveToast(null), 3000);
  }, [forms, formApiService, onUpdateForm]);

  const handleLibrarySelect = useCallback((libraryForm: TemplateBuilderForm) => {
    setLibraryPickerOpen(false);
    if (onAddFormFromLibrary) {
      // Host forks the picked form into a new record — a create-then-attach
      // path that is disabled when the user lacks the combined grant.
      if (!canCreateForms) return;
      onAddFormFromLibrary(libraryForm);
    } else {
      onAddForm('library');
    }
  }, [onAddForm, onAddFormFromLibrary, canCreateForms]);

  const handleReorderSections = useCallback((formId: string, newSections: LayoutSection[]) => {
    const form = forms.get(formId);
    if (!form) return;
    onUpdateForm(formId, { layout: { ...(form.layout ?? {}), sections: newSections } });
  }, [forms, onUpdateForm]);

  // Rule callbacks — template-level rules
  const handleAddRule = useCallback((rule: Rule) => {
    const rules = [...(template.rules || []), rule];
    onUpdateTemplate({ rules });
  }, [template.rules, onUpdateTemplate]);

  const handleUpdateRule = useCallback((rule: Rule) => {
    const rules = (template.rules || []).map((r) => r.id === rule.id ? rule : r);
    onUpdateTemplate({ rules });
  }, [template.rules, onUpdateTemplate]);

  const handleRemoveRule = useCallback((ruleId: string) => {
    const rules = (template.rules || []).filter((r) => r.id !== ruleId);
    onUpdateTemplate({ rules });
  }, [template.rules, onUpdateTemplate]);

  const handleApplyAISuggestion = useCallback((formId: string, fields: Partial<FormField>[]) => {
    const form = forms.get(formId);
    if (!form) return;
    // TODO [AI-WIDGET-PREFERENCE]: When real AI is wired, fields arriving here should already
    // have been resolved against dataBoundWidgetRegistry.ts by CanvasAIPanel before calling
    // onApplySuggestion. Any field with type: 'dataBound' + widgetId should be committed as-is
    // via formService.createWidgetField(widget) using the resolved widgetId, rather than the
    // generic FormField path below. Fields that have no widget match continue through the
    // generic path as plain FormField types.
    const newFields: FormField[] = fields.map((f) => ({
      id: f.id || `ai-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
      label: f.label || '',
      type: f.type || 'text',
      required: f.required ?? false,
      extras: f.extras || {},
    } as FormField));
    const newFieldIds = newFields.map((f) => f.id);

    // A3-02: Append to the currently selected section if one is active, otherwise
    // append to the first section. Also register the new fieldIds in layout.sections.
    const targetSectionId =
      (selection?.level === 'section' ? selection.sectionId : null) ??
      form.layout?.sections?.[0]?.id ??
      null;

    const updatedSections = (form.layout?.sections ?? []).map((s) =>
      s.id === targetSectionId
        ? { ...s, fieldIds: [...s.fieldIds, ...newFieldIds] }
        : s,
    );

    onUpdateForm(formId, {
      fields: [...(form.fields ?? []), ...newFields],
      layout: { ...(form.layout ?? { sections: [] }), sections: updatedSections },
    });
  }, [forms, onUpdateForm, selection]);

  const handleDragOver = useCallback((event: DragOverEvent) => {
    const { active, over } = event;
    const source = active.data.current?.source;
    const translatedRect = active.rect.current.translated ?? active.rect.current.initial ?? null;
    const activeRect = translatedRect
      ? { top: translatedRect.top, height: translatedRect.height }
      : null;
    const overRect = over?.rect
      ? { top: over.rect.top, height: over.rect.height }
      : null;

    // Palette form drag — track whether ghost is over a valid insert slot
    if (source === 'canvasPaletteForm') {
      setIsPaletteFormOverSlot(!!over && String(over.id).startsWith('form-insert-slot-'));
      return;
    }

    if (source === 'canvasPalette' || source === 'canvasPaletteWidget' || source === 'canvasField') {
      if (!over) {
        activeFieldDropTargetRef.current = null;
        setActiveFieldDropTarget(null);
        setDragOverFormId(null);
        return;
      }
      const overId = String(over.id);
      const formIdFromDropzone = overId.startsWith('section-dropzone-') ? overId.replace('section-dropzone-', '') : null;
      const resolvedFormId = formIdFromDropzone ?? (sortedRefs.some((ref) => ref.formId === overId) ? overId : null);
      const pointerY = activeRect ? activeRect.top + activeRect.height / 2 : null;

      // For field-dropzone-* and section-dropzone-* IDs, resolveFieldDropTarget can
      // fall back to stale/default placement while an auto-expanded form is still
      // committing its section DOM. Resolve directly from the pointer when possible
      // so the visual marker reflects actual pointer position.
      const isFieldDropzone = overId.startsWith('field-dropzone-');
      const isSectionDropzone = overId.startsWith('section-dropzone-');
      const fieldDropzoneSectionId = isFieldDropzone ? overId.replace('field-dropzone-', '') : null;
      const fieldDropzoneFormId = fieldDropzoneSectionId ? findSectionLocation(fieldDropzoneSectionId)?.formId ?? null : null;
      const pointerResolvedFormId = fieldDropzoneFormId ?? formIdFromDropzone;

      const target = ((isFieldDropzone || isSectionDropzone)
        ? (pointerResolvedFormId && pointerY != null
            ? resolveInsertFromPointer(pointerResolvedFormId, pointerY)
            : null)
        : resolveFieldDropTarget(overId, activeRect, overRect))
        ?? (resolvedFormId && pointerY != null ? resolveInsertFromPointer(resolvedFormId, pointerY) : null)
        ?? (resolvedFormId ? getDefaultFieldDropTargetForForm(resolvedFormId) : null)
        ?? (fieldDropzoneFormId ? getDefaultFieldDropTargetForForm(fieldDropzoneFormId) : null);

      const hoveredFormId = target?.formId ?? resolvedFormId ?? fieldDropzoneFormId;
      syncHoveredForm(hoveredFormId);
      // Keep the ref in sync so handleDragEnd can read the current target
      // without depending on this state value in its useCallback dep-array.
      activeFieldDropTargetRef.current = target;
      setActiveFieldDropTarget(target);
      return;
    }

    // Palette section drag — track which form block is hovered for auto-expand
    if (source !== 'canvasPaletteSection' && source !== 'canvasSection') return;
    if (!over) { setDragOverFormId(null); return; }
    const overId = String(over.id);
    const fromDropzone = overId.startsWith('section-dropzone-') ? overId.replace('section-dropzone-', '') : null;
    const fromSlot = overId.startsWith('section-insert-slot-') ? overId.replace('section-insert-slot-', '').replace(/-\d+$/, '') : null;
    const fromBlock = !fromDropzone && !fromSlot && sortedRefs.some((r) => r.formId === overId) ? overId : null;
    const hoveredFormId = fromDropzone || fromSlot || fromBlock;
    syncHoveredForm(hoveredFormId);
  }, [findSectionLocation, getDefaultFieldDropTargetForForm, resolveFieldDropTarget, resolveInsertFromPointer, sortedRefs, syncHoveredForm]);

  // Drag-and-drop reorder
  /**
   * @process CanvasDragEnd
   *
   * Steps:
   * 1. Capture the last confirmed drag-over target from the ref before clearing.
   * 2. Reset all transient drag state (form hover, flags, field target, section target).
   * 3. Dispatch drop result to the appropriate branch (form block reorder, palette
   *    form insert, section reorder, palette section, palette field, palette widget,
   *    canvas field move).
   *
   * Inputs:
   * - DragEndEvent from @dnd-kit/core (active item + pointer-release over element).
   * - activeFieldDropTargetRef — always-current ref mirror of activeFieldDropTarget.
   *
   * Outputs:
   * - Local-first form/template mutations through host callbacks.
   * - Optimistic UI state updates (pendingDrop, selection, expansion).
   *
   * Failure Handling:
   * - Invalid/null drop targets leave state unchanged.
   *
   * Idempotency: NOT REQUIRED — each valid drop produces a distinct layout change.
   *
   * @errorHandling
   * Strategy: guard-clause returns on any invalid or locked drop target.
   * User Impact: No-op on invalid drops; layout is never corrupted.
   * Logging: REQUIRED at host workflow boundaries via onUpdateForm callbacks.
   */
  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    const translatedRect = active.rect.current.translated ?? active.rect.current.initial ?? null;
    const activeRect = translatedRect
      ? { top: translatedRect.top, height: translatedRect.height }
      : null;
    const overRect = over?.rect
      ? { top: over.rect.top, height: over.rect.height }
      : null;

    // Capture the last confirmed drag target from the ref BEFORE clearing any state.
    // This is the position the visual marker was showing at the moment of pointer-release.
    // Using the ref avoids the stale-closure problem that would arise from reading
    // activeFieldDropTarget directly (it is not in this useCallback dep-array).
    const lastConfirmedDropTarget = activeFieldDropTargetRef.current;
    activeFieldDropTargetRef.current = null;

    setDragOverFormId(null);
    setIsDraggingPaletteForm(false);
    setIsPaletteFormOverSlot(false);
    setIsDraggingFieldItem(false);
    setActiveFieldDropTarget(null);
    setIsDraggingSection(false);
    dragAutoExpandedFormId.current = null;
    setPendingDrop(null);

    // Palette form item dropped — only act when dropped onto a valid insert slot
    if (active.data.current?.source === 'canvasPaletteForm') {
      if (!over || !String(over.id).startsWith('form-insert-slot-')) return;
      // Every palette form action creates a record — a drag should never reach
      // here when the grant is absent because the items are non-draggable, but
      // guard anyway as the last line of defence before a create-then-attach.
      if (!canCreateForms) return;
      const formItemId = active.data.current?.formItemId as string | undefined;
      if (formItemId === 'palette-new-form') setNewFormOpen(true);
      else if (formItemId === 'palette-from-library') setLibraryPickerOpen(true);
      else if (formItemId === 'palette-header-form') onAddForm('header');
      else if (formItemId === 'palette-system-module' && !allModuleTypesUsed) setModulePickerOpen(true);
      return;
    }

    if (!over) return;
    const overId = String(over.id);
    const formIdFromDropzone = overId.startsWith('section-dropzone-') ? overId.replace('section-dropzone-', '') : null;

    // Resolve formId from field-dropzone-{sectionId}, mirroring the same logic in
    // handleDragOver. handleDragOver uses findSectionLocation; here we iterate forms
    // directly since findSectionLocation is not in this callback's dep array.
    const fieldDropzoneFormId = (() => {
      if (!overId.startsWith('field-dropzone-')) return null;
      const sectionId = overId.replace('field-dropzone-', '');
      for (const [fid, form] of forms) {
        if ((form.layout?.sections ?? []).some((s) => s.id === sectionId)) return fid;
      }
      return null;
    })();

    const resolvedFormId = formIdFromDropzone ?? fieldDropzoneFormId ?? (sortedRefs.some((ref) => ref.formId === overId) ? overId : null);
    const pointerY = activeRect ? activeRect.top + activeRect.height / 2 : null;

    const pointerResolvedFormId = fieldDropzoneFormId ?? formIdFromDropzone;

    // Field/widget drop target resolution:
    // 1. Primary (field/section dropzones): fresh resolveInsertFromPointer at drop time.
    //    By the time handleDragEnd fires all React renders are committed, so section
    //    elements ARE in the DOM — unlike during handleDragOver, where a just-expanded
    //    form may not yet have rendered its sections. This corrects the race where
    //    activeFieldDropTargetRef holds a stale bottom value from a handleDragOver call
    //    that fired before the expanded form's [data-section-id] elements were mounted.
    // 2. Secondary: lastConfirmedDropTarget from the ref (the visual marker position).
    // 3. Tertiary: re-resolve from over.id via slot/field/section ID parsing.
    // 4. Quaternary: pointer-position-based resolution for section-dropzone / form-block.
    // 5. Fallback: default target for the hovered form (last resort to avoid no-op drops).
    const freshPointerTarget = (pointerResolvedFormId && pointerY != null)
      ? resolveInsertFromPointer(pointerResolvedFormId, pointerY)
      : null;
    const fieldDropTarget = freshPointerTarget
      ?? lastConfirmedDropTarget
      ?? resolveFieldDropTarget(overId, activeRect, overRect)
      ?? (resolvedFormId && pointerY != null ? resolveInsertFromPointer(resolvedFormId, pointerY) : null)
      ?? (resolvedFormId ? getDefaultFieldDropTargetForForm(resolvedFormId) : null);

    if (active.data.current?.source === 'canvasField') {
      if (fieldDropTarget) {
        moveFieldIntoSection(String(active.id), fieldDropTarget);
      }
      return;
    }

    if (active.data.current?.source === 'canvasSection') {
      const sourceFormId = active.data.current?.formId as string | undefined;
      const form = sourceFormId ? forms.get(sourceFormId) : null;
      if (!sourceFormId || !form || form.formKind === 'module' || form.formKind === 'header') return;
      const sections = [...(form.layout?.sections ?? [])].sort((a, b) => a.order - b.order);
      const oldIndex = sections.findIndex((section) => section.id === active.id);
      if (oldIndex === -1) return;

      const slotMatch = overId.startsWith('section-insert-slot-') ? /^section-insert-slot-(.+)-(\d+)$/.exec(overId) : null;
      const dropzoneSectionId = overId.startsWith('field-dropzone-') ? overId.replace('field-dropzone-', '') : null;
      const targetSectionId = dropzoneSectionId ?? overId;
      const targetSectionIndex = sections.findIndex((section) => section.id === targetSectionId);
      const slotFormId = slotMatch?.[1] ?? null;
      const rawNewIndex = slotMatch ? Number(slotMatch[2]) : targetSectionIndex;

      if ((slotFormId && slotFormId !== sourceFormId) || rawNewIndex === -1 || Number.isNaN(rawNewIndex)) return;
      const adjustedIndex = rawNewIndex > oldIndex ? rawNewIndex - 1 : rawNewIndex;
      const reordered = arrayMove(sections, oldIndex, Math.max(0, Math.min(sections.length - 1, adjustedIndex))).map((section, index) => ({ ...section, order: index }));
      handleReorderSections(sourceFormId, reordered);
      return;
    }

    // Palette section dropped — open modal to collect section properties
    if (active.data.current?.source === 'canvasPaletteSection') {
      const targetFormId = overId.startsWith('section-dropzone-')
        ? overId.replace('section-dropzone-', '')
        : overId.startsWith('section-insert-slot-')
          ? overId.replace('section-insert-slot-', '').replace(/-\d+$/, '')
          : null;
      if (targetFormId) {
        const fkind = forms.get(targetFormId)?.formKind;
        if (fkind !== 'module' && fkind !== 'header') {
          setSelectedFormId(targetFormId);
          setPendingSectionDrop(targetFormId);
        }
      }
      return;
    }

    // Palette field type dropped onto a section's field-drop zone → insert a normal field row.
    if (active.data.current?.source === 'canvasPalette' && active.data.current?.fieldType) {
      if (fieldDropTarget) {
        const fieldType: FieldType = active.data.current.fieldType;
        const fkind3 = forms.get(fieldDropTarget.formId)?.formKind;
        if (fkind3 !== 'module' && fkind3 !== 'header') {
          const newField = formService.createBlankField(fieldType);
          insertFieldIntoSection(fieldDropTarget.formId, fieldDropTarget.sectionId, fieldDropTarget.insertIndex, newField);
        }
      }
      return;
    }

    if (active.data.current?.source === 'canvasPaletteWidget' && active.data.current?.widget) {
      if (fieldDropTarget) {
        const fkindWidget = forms.get(fieldDropTarget.formId)?.formKind;
        if (fkindWidget === 'module' || fkindWidget === 'header') return;
        const newField = formService.createWidgetField(active.data.current.widget as DataBoundWidgetDefinition);
        insertFieldIntoSection(fieldDropTarget.formId, fieldDropTarget.sectionId, fieldDropTarget.insertIndex, newField);
      }
      return;
    }

    if (active.id === over.id) return;
    const oldIndex = sortedRefs.findIndex((r) => r.formId === active.id);
    const newIndex = sortedRefs.findIndex((r) => r.formId === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const reordered = arrayMove(sortedRefs, oldIndex, newIndex).map((r, i) => ({ ...r, order: i + 1 }));
    onReorderForms(reordered);
  }, [sortedRefs, forms, onReorderForms, onAddForm, allModuleTypesUsed, canCreateForms, resolveFieldDropTarget, insertFieldIntoSection, moveFieldIntoSection, resolveInsertFromPointer, getDefaultFieldDropTargetForForm, handleReorderSections]);

  // Resize handlers
  const handlePanelResize = useCallback((delta: number) => {
    setPanelWidth((w) => {
      const next = Math.min(MAX_W, Math.max(MIN_W, w + delta));
      saveW(PANEL_WIDTH_KEY, next);
      return next;
    });
  }, []);

  // Field palette handlers — add field/widget to selected form
  const handleAddField = useCallback((type: FieldType) => {
    if (!selectedFormId) return;
    const form = forms.get(selectedFormId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return;
    const newField = formService.createBlankField(type);
    const fields = [...(form.fields ?? []), newField];
    const sections = [...(form.layout?.sections ?? [])];
    if (sections.length > 0) {
      const selectedSectionId = selection?.level === 'section' ? (selection as { level: 'section'; sectionId: string }).sectionId : null;
      const targetIdx = selectedSectionId ? sections.findIndex((s) => s.id === selectedSectionId) : -1;
      const idx = targetIdx !== -1 ? targetIdx : sections.length - 1;
      const target = { ...sections[idx] };
      target.fieldIds = [...target.fieldIds, newField.id];
      sections[idx] = target;
    }
    onUpdateForm(selectedFormId, { fields, layout: { ...form.layout, sections } });
    setSelection({ level: 'field', fieldId: newField.id });
  }, [selectedFormId, forms, onUpdateForm, selection]);

  const handleAddWidget = useCallback((widget: DataBoundWidgetDefinition) => {
    if (!selectedFormId) return;
    const form = forms.get(selectedFormId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return;
    const newField = formService.createWidgetField(widget);
    const fields = [...(form.fields ?? []), newField];
    const sections = [...(form.layout?.sections ?? [])];
    if (sections.length > 0) {
      const selectedSectionId = selection?.level === 'section' ? (selection as { level: 'section'; sectionId: string }).sectionId : null;
      const targetIdx = selectedSectionId ? sections.findIndex((s) => s.id === selectedSectionId) : -1;
      const idx = targetIdx !== -1 ? targetIdx : sections.length - 1;
      const target = { ...sections[idx] };
      target.fieldIds = [...target.fieldIds, newField.id];
      sections[idx] = target;
    }
    onUpdateForm(selectedFormId, { fields, layout: { ...form.layout, sections } });
    setSelection({ level: 'field', fieldId: newField.id });
  }, [selectedFormId, forms, onUpdateForm, selection]);

  const handleAddWidgets = useCallback((widgets: DataBoundWidgetDefinition[]) => {
    if (!selectedFormId) return;
    const form = forms.get(selectedFormId);
    if (!form || form.formKind === 'module' || form.formKind === 'header') return;
    const newFields = widgets.map((w) => formService.createWidgetField(w));
    const fields = [...(form.fields ?? []), ...newFields];
    const sections = [...(form.layout?.sections ?? [])];
    if (sections.length > 0) {
      const selectedSectionId = selection?.level === 'section' ? (selection as { level: 'section'; sectionId: string }).sectionId : null;
      const targetIdx = selectedSectionId ? sections.findIndex((s) => s.id === selectedSectionId) : -1;
      const idx = targetIdx !== -1 ? targetIdx : sections.length - 1;
      const target = { ...sections[idx] };
      target.fieldIds = [...target.fieldIds, ...newFields.map((f) => f.id)];
      sections[idx] = target;
    }
    onUpdateForm(selectedFormId, { fields, layout: { ...form.layout, sections } });
    if (newFields.length > 0) setSelection({ level: 'field', fieldId: newFields[newFields.length - 1].id });
  }, [selectedFormId, forms, onUpdateForm, selection]);

  const handleCommitPendingDrop = useCallback(({ label, required }: { label: string; required: boolean }) => {
    if (!pendingDrop) return;
    const { fieldType, sectionId, formId, insertIndex } = pendingDrop;
    const form = forms.get(formId);
    if (!form) { setPendingDrop(null); return; }
    const newField = formService.createBlankField(fieldType);
    if (label.trim()) newField.label = label.trim();
    if (required) newField.required = true;
    insertFieldIntoSection(formId, sectionId, insertIndex, newField);
    setPendingDrop(null);
  }, [pendingDrop, forms, insertFieldIntoSection]);

  const handleCancelPendingDrop = useCallback(() => {
    setPendingDrop(null);
  }, []);

  /**
   * @process CreateNewForm
   *
   * Steps:
   * 1. Validate name and formApiService presence.
   * 2. Close the dialog optimistically before the async create resolves.
   * 3. Create the form through the host-injected local-first formApiService.
   * 4. Reset dialog fields.
   * 5. Notify host via onNewFormAdded so it updates its Dexie-backed map and template refs.
   *
   * Inputs:
   * - Dialog state: newFormName, newFormDesc, newFormCategory, newFormColumns, newFormHidden
   * - template.templateType / templateTypeName for the new form record
   *
   * Outputs:
   * - New TemplateBuilderForm record created in local Dexie through formApiService.createForm
   *
   * Failure Handling:
   * - If formApiService is absent the call is a no-op; button is disabled in that case.
   *
   * Idempotency: NOT REQUIRED — each invocation intentionally creates a distinct form record.
   */
  const handleCreateNewForm = useCallback(async () => {
    if (!newFormName.trim() || !formApiService || !canCreateForms) return;
    setNewFormOpen(false);
    const newForm = await formApiService.createForm({
      name: newFormName.trim(),
      description: newFormDesc.trim() || undefined,
      category: newFormCategory.trim() || undefined,
      templateType: template.templateType,
      templateTypeName: (template as any).templateTypeName,
      initialVisibility: newFormHidden ? 'hidden' : 'shown',
      fields: [],
      layout: {
        sections: [{
          id: `section-${Date.now()}`,
          title: 'Section 1',
          columns: newFormColumns,
          order: 0,
          fieldIds: [],
        }],
      },
    });
    setNewFormName('');
    setNewFormDesc('');
    setNewFormCategory('');
    setNewFormColumns(1);
    setNewFormHidden(false);
    if (onNewFormAdded) onNewFormAdded(newForm);
  }, [newFormName, newFormDesc, newFormCategory, newFormColumns, newFormHidden, formApiService, template, onNewFormAdded, canCreateForms]);

  // Derive the selected form/field/section for props panel
  const selectedForm = selectedFormId ? forms.get(selectedFormId) : null;

  const selectedField = selection?.level === 'field' && selectedForm
    ? selectedForm.fields.find((f) => f.id === (selection as { level: 'field'; fieldId: string }).fieldId) || null
    : null;
  const selectedSection = selection?.level === 'section' && selectedForm
    ? selectedForm.layout.sections.find((s) => s.id === (selection as { level: 'section'; sectionId: string }).sectionId) || null
    : null;

  // Auto-open properties panel when something is selected
  useEffect(() => {
    if (selection && activePanel !== 'properties') {
      setActivePanel('properties');
      saveActivePanel('properties');
    }
  }, [selection]); // eslint-disable-line react-hooks/exhaustive-deps

  const panelOpen = activePanel !== null;
  const toolWindowWidth = panelOpen ? panelWidth + 32 : 32;

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {/* Toolbar */}
      <div ref={toolbarRef} className="relative shrink-0">
        <TemplateCanvasToolbar
          mode={mode}
          onModeChange={setMode}
          formCount={sortedRefs.length}
          ruleCount={template.rules?.length || 0}
          showRuleLines={showRuleLines}
          onToggleRuleLines={() => setShowRuleLines((v) => !v)}
          hasAI={hasAI}
          aiOpen={aiOpen}
          onToggleAI={() => setAIOpen((v) => !v)}
          aiTokensUsed={aiTokensUsed}
          aiSessionBudget={aiCostRails?.sessionTokenBudget ?? 0}
        />
        {/* Section 2.8.3: Settings dropdown removed. */}
      </div>

      {/* Body — DndContext wraps palette + canvas so they share drag/drop */}
      <DndContext
        sensors={sensors}
        collisionDetection={pointerWithin}
        onDragEnd={handleDragEnd}
        onDragOver={handleDragOver}
        onDragStart={(e) => {
          if (e.active.data.current?.source === 'canvasPaletteForm') setIsDraggingPaletteForm(true);
          if (e.active.data.current?.source === 'canvasPalette' || e.active.data.current?.source === 'canvasPaletteWidget' || e.active.data.current?.source === 'canvasField') setIsDraggingFieldItem(true);
          if (e.active.data.current?.source === 'canvasPaletteSection' || e.active.data.current?.source === 'canvasSection') setIsDraggingSection(true);
        }}
        onDragCancel={() => { activeFieldDropTargetRef.current = null; setDragOverFormId(null); setIsDraggingPaletteForm(false); setIsPaletteFormOverSlot(false); setIsDraggingFieldItem(false); setActiveFieldDropTarget(null); setIsDraggingSection(false); dragAutoExpandedFormId.current = null; setPendingDrop(null); }}
      >
      {/* Body row — the field tray and right tool window reserve real layout
          width. The center canvas is the remaining dynamic white space, so the
          form work area stays centered as either side expands or contracts. */}
      <div className="flex-1 flex overflow-hidden min-w-0 relative">
        {/* Master Component Tray — always visible in build mode */}
        {mode === 'build' && (
          <CanvasFieldPalette
            templateType={template.templateType as TemplateType}
            activeFormFields={selectedForm?.fields || []}
            activeFormName={selectedForm?.name || null}
            hasHeaderForm={hasHeaderForm}
            selectedFormIsModule={selectedForm?.formKind === 'module'}
            selectedFormIsHeader={selectedForm?.formKind === 'header'}
            existingModuleTypes={existingModuleTypes}
            selectionContext={
              !selectedFormId ? 'none'
              : selection?.level === 'field' ? 'field'
              : selection?.level === 'section' ? 'section'
              : 'form'
            }
            onAddField={handleAddField}
            onAddWidget={handleAddWidget}
            onAddWidgets={handleAddWidgets}
            onAddNewForm={() => setNewFormOpen(true)}
            onAddFromLibrary={() => setLibraryPickerOpen(true)}
            onAddModule={() => setModulePickerOpen(true)}
            onAddHeaderForm={() => onAddForm('header')}
            onAddSection={() => selectedFormId ? setPendingSectionDrop(selectedFormId) : undefined}
            canCreateForms={canCreateForms}
          />
        )}

        {/* Preview mode — full-height, outside scroll area */}
        {mode === 'preview' && PreviewRenderer && (
          <PreviewRenderer template={template} forms={forms} />
        )}

        {/* Canvas scroll area (build only) */}
        <div
          ref={canvasScrollRef}
          aria-label="Template canvas work area"
          className={`flex-1 overflow-y-auto overflow-x-hidden bg-gray-100 relative ${mode === 'preview' ? 'hidden' : ''}`}
          style={{ minWidth: mode === 'build' ? 450 : undefined }}
        >
          {mode === 'build' && sortedRefs.length === 0 && (
            <CanvasEmptyState
              canAddModule={!allModuleTypesUsed}
              canCreateForms={canCreateForms}
              onChoose={(choice) => {
                if (choice === 'new') { setNewFormOpen(true); }
                else if (choice === 'library') { setLibraryPickerOpen(true); }
                else if (choice === 'module') { setModulePickerOpen(true); }
                else { onAddForm(choice); }
              }}
            />
          )}
          {mode === 'build' && sortedRefs.length > 0 && (
          <div className="flex justify-center w-full py-4 px-4">
          <div className="w-full max-w-2xl space-y-3">
            <SortableContext
                  items={sortedRefs.map((r) => r.formId)}
                  strategy={verticalListSortingStrategy}
                >
                  {/* Slot before first block */}
                  <FormInsertSlot index={0} isActive={isDraggingPaletteForm} />
                  {sortedRefs.map((ref, idx) => {
                    const form = forms.get(ref.formId);
                    if (!form) {
                      return (
                        <Fragment key={ref.formId}>
                          <div className="rounded-xl border border-dashed border-gray-300 bg-white p-4 text-xs text-gray-400 text-center">
                            Loading form…
                          </div>
                          <FormInsertSlot index={idx + 1} isActive={isDraggingPaletteForm} />
                        </Fragment>
                      );
                    }
                    return (
                      <Fragment key={ref.formId}>
                      <SortableFormBlockWrapper
                        formRef={ref}
                        form={form}
                        isSelected={selectedFormId === ref.formId}
                        isExpanded={expandedForms.has(ref.formId) || dragOverFormId === ref.formId}
                        isDropHover={isDraggingFieldItem && dragOverFormId === ref.formId}
                        selection={selectedFormId === ref.formId ? selection : null}
                        onSelectForm={() => handleSelectForm(ref.formId)}
                        onToggleExpansion={() => handleToggleExpansion(ref.formId)}
                        onSelectSection={(sectionId) => { setSelectedFormId(ref.formId); handleSelectSection(sectionId); }}
                        onSelectField={(fieldId) => {
                          setSelectedFormId(ref.formId);
                          setExpandedForms((prev) => { const next = new Set(prev); next.add(ref.formId); return next; });
                          handleSelectField(fieldId);
                        }}
                        onRemoveForm={() => onRemoveFormRef(ref.formId)}
                        onUpdateField={handleUpdateField}
                        onUpdateSection={handleUpdateSection}
                        onAddSection={() => setPendingSectionDrop(ref.formId)}
                        onReorderSections={handleReorderSections}
                        onToggleFavorite={() => handleToggleFavorite(ref.formId)}
                        onUpdateLibraryForm={() => handleUpdateLibraryForm(ref.formId)}
                        onRemoveFromLibrary={() => handleRemoveFromLibrary(ref.formId)}
                        onUpdateForm={(updates) => onUpdateForm(ref.formId, updates)}
                        ruleCount={rulesPerForm(ref.formId)}
                        internalRuleCount={internalRuleCounts.get(ref.formId) ?? 0}
                        pendingDropSectionId={pendingDrop?.formId === ref.formId ? pendingDrop.sectionId : null}
                        pendingDropFieldType={pendingDrop?.formId === ref.formId ? pendingDrop.fieldType : null}
                        pendingDropInsertIndex={pendingDrop?.formId === ref.formId ? pendingDrop.insertIndex : null}
                        activeFieldDropSectionId={activeFieldDropTarget?.formId === ref.formId ? activeFieldDropTarget.sectionId : null}
                        activeFieldDropInsertIndex={activeFieldDropTarget?.formId === ref.formId ? activeFieldDropTarget.insertIndex : null}
                        showFieldDropTargets={isDraggingFieldItem}
                        onCommitDrop={handleCommitPendingDrop}
                        onCancelDrop={handleCancelPendingDrop}
                        isDraggingSection={isDraggingSection}
                        onDeleteField={(fieldId) => {
                          const f = forms.get(ref.formId);
                          if (!f) return;
                          const fields = (f.fields ?? []).filter((field) => field.id !== fieldId);
                          const sections = (f.layout?.sections ?? []).map((s) => ({
                            ...s, fieldIds: s.fieldIds.filter((id) => id !== fieldId),
                          }));
                          onUpdateForm(ref.formId, { fields, layout: { ...(f.layout ?? {}), sections } });
                          setSelection(null);
                        }}
                        onDuplicateField={(fieldId, sectionId) => handleDuplicateField(ref.formId, fieldId, sectionId)}
                        onUpgradeFormRef={onUpgradeFormRef}
                        onRegisterRef={(fid: string, el: HTMLElement | null) => {
                          if (el) formBlockRefs.current.set(fid, el);
                          else formBlockRefs.current.delete(fid);
                        }}
                      />
                      <FormInsertSlot index={idx + 1} isActive={isDraggingPaletteForm} />
                      </Fragment>
                    );
                  })}
                </SortableContext>

            {/* Add form bar — every action here creates a form record that is
                immediately attached via formRefs, so the bar is hidden when the
                host reports the combined create+edit grant is absent. */}
            {canCreateForms && (
            <div className="w-full flex items-stretch border border-dashed border-gray-300 rounded-xl overflow-hidden hover:border-gray-400 transition-colors bg-white">
              <button
                onClick={() => setNewFormOpen(true)}
                title="Add a blank form"
                className="flex-1 flex flex-col items-center justify-center gap-1.5 py-3 text-gray-500 hover:bg-blue-50 transition-colors group"
              >
                <div className="w-7 h-7 rounded-md bg-blue-50 group-hover:bg-blue-100 flex items-center justify-center transition-colors">
                  <Plus size={14} className="text-blue-600" />
                </div>
                <span className="text-[11px] font-medium text-gray-600 group-hover:text-blue-700">New Form</span>
              </button>
              <div className="w-px bg-gray-200 self-stretch" />
              <button
                onClick={() => setLibraryPickerOpen(true)}
                title="Add a form from your library"
                className="flex-1 flex flex-col items-center justify-center gap-1.5 py-3 text-gray-500 hover:bg-violet-50 transition-colors group"
              >
                <div className="w-7 h-7 rounded-md bg-violet-50 group-hover:bg-violet-100 flex items-center justify-center transition-colors">
                  <FolderOpen size={14} className="text-violet-600" />
                </div>
                <span className="text-[11px] font-medium text-gray-600 group-hover:text-violet-700">From Library</span>
              </button>
              {!hasHeaderForm && (
                <>
                  <div className="w-px bg-gray-200 self-stretch" />
                  <button
                    onClick={() => onAddForm('header')}
                    title="Add a rich-text header / instructions form"
                    className="flex-1 flex flex-col items-center justify-center gap-1.5 py-3 text-gray-500 hover:bg-amber-50 transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-md bg-amber-50 group-hover:bg-amber-100 flex items-center justify-center transition-colors">
                      <FileText size={14} className="text-amber-600" />
                    </div>
                    <span className="text-[11px] font-medium text-gray-600 group-hover:text-amber-700">Header Form</span>
                  </button>
                </>
              )}
              {!allModuleTypesUsed && (
                <>
                  <div className="w-px bg-gray-200 self-stretch" />
                  <button
                    onClick={() => setModulePickerOpen(true)}
                    title="Add a system module (Contacts, Violations…)"
                    className="flex-1 flex flex-col items-center justify-center gap-1.5 py-3 text-gray-500 hover:bg-teal-50 transition-colors group"
                  >
                    <div className="w-7 h-7 rounded-md bg-teal-50 group-hover:bg-teal-100 flex items-center justify-center transition-colors">
                      <Cpu size={14} className="text-teal-600" />
                    </div>
                    <span className="text-[11px] font-medium text-gray-600 group-hover:text-teal-700">System Module</span>
                  </button>
                </>
              )}
            </div>
            )}
          </div>
          </div>
          )}

          {/* Rule connection lines overlay */}
          {mode === 'build' && showRuleLines && (template.rules?.length || 0) > 0 && (
            <RuleConnectionLines
              template={template}
              forms={forms}
              formBlockRefs={formBlockRefs.current}
              containerRef={canvasScrollRef as React.RefObject<HTMLElement>}
              visible={showRuleLines}
              formIds={sortedFormIds}
              hoveredRuleId={hoveredRuleId}
              onHoverRule={setHoveredRuleId}
            />
          )}

          {/* Rule lines legend — slide-in panel paired with the overlay */}
          {mode === 'build' && showRuleLines && (
            <RuleLinesLegend
              template={template}
              forms={forms}
              open={showRuleLines}
              onClose={() => setShowRuleLines(false)}
              hoveredRuleId={hoveredRuleId}
              onHoverRule={setHoveredRuleId}
            />
          )}
        </div>

        {/* AI Panel (right slide-in — independent of tool window system) */}
        {aiOpen && hasAI && mode === 'build' && (
          <CanvasAIPanel
            template={template}
            forms={forms}
            onApplySuggestion={handleApplyAISuggestion}
            onClose={() => setAIOpen(false)}
            tokensUsed={aiTokensUsed}
            sessionBudget={aiCostRails?.sessionTokenBudget ?? 0}
            onRecordTokens={recordAITokens ?? (() => {})}
            onGenerateSuggestions={onGenerateAISuggestions}
            onParseDocument={onParseAIDocument}
          />
        )}

        {/* ── VS-style tool window system ── */}
        {mode === 'build' && (
          <div
            aria-label="Template canvas tool windows"
            className="relative z-30 flex h-full shrink-0"
            style={{ width: toolWindowWidth }}
          >

            {/* Open panel — pinned = layout space, unpinned = absolute overlay */}
            {panelOpen && (
              <div
                className="relative h-full bg-white border-l border-gray-200 overflow-hidden shadow-xl pointer-events-auto"
                style={{ width: panelWidth }}
              >
                <ResizeHandle onResize={handlePanelResize} />
                <div className="flex h-full flex-col overflow-hidden pl-3">
                  {/* Panel header */}
                  <div className="flex items-center justify-between px-3 py-2 border-b bg-gray-50 shrink-0">
                    <span className="text-xs font-semibold text-gray-600">
                      {activePanel === 'properties' ? 'Properties' : activePanel === 'rules' ? 'Rules' : 'Violations'}
                    </span>
                    <div className="flex items-center gap-0.5">
                      <button
                        onClick={handleClosePanel}
                        title="Close"
                        className="p-1 rounded-sm text-gray-400 hover:text-gray-700 transition-colors"
                      >
                        <XIcon size={13} />
                      </button>
                    </div>
                  </div>

                  {/* Panel content */}
                  <div className="flex-1 overflow-hidden">
                    {activePanel === 'properties' && (
                      <PropertiesPanel
                        selection={selection}
                        field={selectedField}
                        section={selectedSection}
                        form={selectedForm}
                        sections={selectedForm?.layout.sections || []}
                        allFields={selectedForm?.fields || []}
                        rules={template.rules || []}
                        onUpdateField={handleUpdateField}
                        onUpdateSection={handleUpdateSection}
                        onUpdateForm={(updates) => { if (selectedFormId) onUpdateForm(selectedFormId, updates); }}
                        onDeleteField={(fieldId) => {
                          if (!selectedFormId) return;
                          const f = forms.get(selectedFormId);
                          if (!f) return;
                          const fields = (f.fields ?? []).filter((field) => field.id !== fieldId);
                          const sections = (f.layout?.sections ?? []).map((s) => ({
                            ...s,
                            fieldIds: s.fieldIds.filter((id) => id !== fieldId),
                          }));
                          onUpdateForm(selectedFormId, { fields, layout: { ...(f.layout ?? {}), sections } });
                          setSelection(null);
                        }}
                        onAddRule={handleAddRule}
                        onUpdateRule={handleUpdateRule}
                        onRemoveRule={handleRemoveRule}
                        onClose={handleClosePanel}
                        templateName={template.name}
                        templateType={template.templateTypeName}
                        templateStatus={template.status}
                        categoryPool={[...new Set([...forms.values()].map((f) => f.category).filter(Boolean) as string[])].sort()}
                        dexieDb={dexieDb}
                        asyncViolationRanker={asyncViolationRanker}
                      />
                    )}
                    {activePanel === 'rules' && (
                      <CanvasRulesPanel
                        template={template}
                        forms={forms}
                        onUpdate={onUpdateTemplate}
                        onClose={handleClosePanel}
                        selection={selection}
                      />
                    )}
                    {activePanel === 'violations' && (
                      <CanvasViolationsPanel
                        template={template}
                        forms={forms}
                        activeCodeSetId={activeViolationCodeSetId}
                        activeCodeEntries={activeViolationCodeEntries}
                        dexieDb={dexieDb}
                        onClose={handleClosePanel}
                        onChangeViolationMapping={handleChangeViolationMapping}
                        onRevealField={revealField}
                      />
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Tab strip — always visible on right edge */}
            <div className="w-8 flex flex-col items-center pt-3 gap-1 bg-white border-l border-gray-200 shadow-xs shrink-0 pointer-events-auto">
              <button
                onClick={() => handleTogglePanel('properties')}
                title="Properties"
                className={`w-7 flex flex-col items-center gap-0.5 py-2 rounded-md border transition-colors ${
                  activePanel === 'properties'
                    ? 'bg-orange-50 border-orange-200 text-(--brand-primary) shadow-xs'
                    : 'border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <Settings2 size={13} />
                <span className="text-[8px] font-semibold tracking-wide leading-none [writing-mode:vertical-rl] rotate-180">
                  Properties
                </span>
              </button>
              <button
                onClick={() => handleTogglePanel('rules')}
                title="Rules"
                className={`w-7 flex flex-col items-center gap-0.5 py-2 rounded-md border transition-colors ${
                  activePanel === 'rules'
                    ? 'bg-orange-50 border-orange-200 text-(--brand-primary) shadow-xs'
                    : 'border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <GitBranch size={13} />
                <span
                  className={`text-[8px] font-semibold tracking-wide leading-none [writing-mode:vertical-rl] rotate-180 ${
                    (template.rules?.length || 0) > 0 ? 'text-blue-500' : ''
                  }`}
                >
                  Rules
                </span>
                {(template.rules?.length || 0) > 0 && (
                  <span className="text-[8px] font-bold text-blue-500 leading-none">{template.rules!.length}</span>
                )}
              </button>
              {/* Section 2.8.6: Violations tab */}
              <button
                onClick={() => handleTogglePanel('violations')}
                title="Violations"
                className={`w-7 flex flex-col items-center gap-0.5 py-2 rounded-md border transition-colors ${
                  activePanel === 'violations'
                    ? 'bg-orange-50 border-orange-200 text-(--brand-primary) shadow-xs'
                    : 'border-transparent text-gray-400 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <AlertTriangle size={13} />
                <span className="text-[8px] font-semibold tracking-wide leading-none [writing-mode:vertical-rl] rotate-180">
                  Violations
                </span>
              </button>
            </div>
          </div>
        )}
      </div>{/* end body flex row */}

      {/* DragOverlay — palette form drags, form-block reordering, field/widget/section drags */}
      <DragOverlay dropAnimation={null}>
        {isDraggingPaletteForm ? (
          isPaletteFormOverSlot ? (
            <div className="rounded-xl border-2 border-dashed border-(--brand-primary) bg-orange-50 shadow-lg px-4 py-3 text-sm font-medium text-orange-700 flex items-center gap-2 opacity-90">
              <GripVertical size={14} className="text-orange-400" /> Drop to add form
            </div>
          ) : (
            <div className="rounded-xl border-2 border-dashed border-red-300 bg-red-50 shadow-lg px-4 py-3 text-sm font-medium text-red-400 flex items-center gap-2 opacity-75 cursor-no-drop">
              <GripVertical size={14} className="text-red-300" /> Not a valid drop target
            </div>
          )
        ) : isDraggingFieldItem ? (
          <div className="rounded-lg border-2 border-dashed border-blue-400 bg-blue-50 shadow-lg px-4 py-2.5 text-sm font-medium text-blue-700 flex items-center gap-2 opacity-90 pointer-events-none">
            <Database size={14} className="text-blue-500" /> Place field or widget
          </div>
        ) : isDraggingSection ? (
          <div className="rounded-lg border-2 border-dashed border-purple-400 bg-purple-50 shadow-lg px-4 py-2.5 text-sm font-medium text-purple-700 flex items-center gap-2 opacity-90 pointer-events-none">
            <GripVertical size={14} className="text-purple-400" /> Place section on form
          </div>
        ) : (
          <div className="rounded-xl border bg-white shadow-lg px-4 py-3 text-sm font-medium text-gray-600 flex items-center gap-2">
            <GripVertical size={14} className="text-gray-400" /> Moving form…
          </div>
        )}
      </DragOverlay>
      </DndContext>{/* end shared DndContext */}

      {saveToast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-200 px-4 py-2.5 bg-gray-900 text-white text-xs rounded-xl shadow-xl flex items-center gap-2 animate-fade-in">
          <span className="text-green-400">✓</span> {saveToast}
        </div>
      )}
      {libraryPickerOpen && memoizedFormLibraryService && (
        <FormLibraryPicker
          templateType={template.templateType as TemplateType}
          onSelect={handleLibrarySelect}
          onClose={() => setLibraryPickerOpen(false)}
          formService={memoizedFormLibraryService}
        />
      )}
      {pendingSectionDrop && (
        <NewSectionModal
          onConfirm={(cfg) => {
            handleAddSection(pendingSectionDrop, cfg);
            setPendingSectionDrop(null);
          }}
          onCancel={() => setPendingSectionDrop(null)}
        />
      )}
      {modulePickerOpen && (
        <ModulePickerModal
          disabledTypes={existingModuleTypes}
          violationCodeSetService={violationCodeSetService}
          allowDeferViolations={allowDeferViolations}
          onClose={() => setModulePickerOpen(false)}
          onConfirm={async (_moduleType: ModuleType, config: ContactsModuleConfig | ViolationsModuleConfig, name: string) => {
            setModulePickerOpen(false);
            if (!formApiService || !canCreateForms) { setModulePickerOpen(false); return; }
            const newForm = await formApiService.createForm({
              name,
              formKind: 'module',
              moduleConfig: config,
              fields: [],
              layout: { sections: [] },
            });
            if (onAddModuleForm) onAddModuleForm(newForm);
          }}
        />
      )}
      {newFormOpen && (
        <div className="fixed inset-0 z-200 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/50"
            onClick={() => {
              setNewFormOpen(false);
              setNewFormName(''); setNewFormDesc(''); setNewFormCategory('');
              setNewFormColumns(1); setNewFormHidden(false);
            }}
          />
          <div
            className="relative bg-white rounded-xl shadow-2xl w-full max-w-md p-6 flex flex-col gap-5"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-semibold text-gray-900 text-base">New Form</h3>
                <p className="text-xs text-gray-400 mt-0.5">This form will be added to the canvas.</p>
              </div>
              <button
                title="Close"
                onClick={() => {
                  setNewFormOpen(false);
                  setNewFormName(''); setNewFormDesc(''); setNewFormCategory('');
                  setNewFormColumns(1); setNewFormHidden(false);
                }}
                className="text-gray-400 hover:text-gray-600 p-1 rounded-lg hover:bg-gray-100"
              >
                <XIcon size={16} />
              </button>
            </div>

            <div className="flex flex-col gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1" htmlFor="canvas-new-form-name">
                  Form name <span className="text-red-500">*</span>
                </label>
                <input
                  id="canvas-new-form-name"
                  autoFocus
                  type="text"
                  value={newFormName}
                  onChange={(e) => setNewFormName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && newFormName.trim()) void handleCreateNewForm(); }}
                  placeholder="e.g. Interior Inspection"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary)/40"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Description <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newFormDesc}
                  onChange={(e) => setNewFormDesc(e.target.value)}
                  placeholder="e.g. Covers all interior fire safety checks"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary)/40"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Category <span className="text-gray-400 font-normal">(optional)</span>
                </label>
                <input
                  type="text"
                  value={newFormCategory}
                  onChange={(e) => setNewFormCategory(e.target.value)}
                  placeholder="e.g. Safety, Electrical, Plumbing"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-hidden focus:ring-2 focus:ring-(--brand-primary)/40"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Columns</label>
                  <div className="flex items-center bg-gray-100 rounded-sm p-0.5 gap-px w-fit">
                    {([1, 2, 3] as const).map((n) => (
                      <button
                        key={n}
                        type="button"
                        onClick={() => setNewFormColumns(n)}
                        className={`w-7 h-7 rounded text-xs font-semibold transition-colors ${
                          newFormColumns === n
                            ? 'bg-(--brand-primary) text-white shadow-xs'
                            : 'text-gray-400 hover:text-gray-600 hover:bg-white/60'
                        }`}
                        title={`${n} column${n > 1 ? 's' : ''}`}
                      >
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Initial visibility</label>
                  <button
                    type="button"
                    onClick={() => setNewFormHidden((v) => !v)}
                    className={`w-full py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                      newFormHidden
                        ? 'bg-amber-50 border-amber-400 text-amber-700'
                        : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {newFormHidden ? 'Starts hidden' : 'Starts visible'}
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-gray-100">
              <button
                onClick={() => {
                  setNewFormOpen(false);
                  setNewFormName(''); setNewFormDesc(''); setNewFormCategory('');
                  setNewFormColumns(1); setNewFormHidden(false);
                }}
                className="px-3 py-1.5 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                disabled={!newFormName.trim() || !formApiService || !canCreateForms}
                onClick={() => void handleCreateNewForm()}
                className="px-4 py-1.5 text-sm font-medium text-white rounded-lg disabled:opacity-40 disabled:cursor-not-allowed transition-colors bg-(--brand-primary) hover:brightness-95"
              >
                Add to Canvas
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TemplateCanvas;
